import React, { useCallback, useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import StepXToast from 'components/common/StepXToast';
import classNames from 'classnames';
import { ToastContext } from 'contexts/ToastContext';
import { ToastData, ToastPosition, ToastContextType } from 'shared/types/toast';
import { setGlobalToastInstance } from 'shared/helpers/stepx-toast';

interface StepXToastProviderProps {
  children: ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

export const StepXToastProvider = ({ children, position = 'top-right', maxToasts = 5 }: StepXToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const toastIdCounter = useRef(0);
  const timeoutRefs = useRef(new Set<number>());

  const generateId = useCallback(() => {
    toastIdCounter.current += 1;
    return `stepx-toast-${toastIdCounter.current}`;
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
    // Clear all timeouts
    timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutRefs.current.clear();
  }, []);

  const showToast = useCallback((toastData: Omit<ToastData, 'id'>) => {
    const id = generateId();
    const toast: ToastData = {
      id,
      duration: 5000, // Default 5 seconds
      position: position,
      showProgress: true,
      closeButton: true,
      ...toastData,
    };

    setToasts(prev => {
      // Use functional update to avoid stale closure
      const newToasts = [...prev, toast];
      
      // Limit number of toasts
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts);
      }
      
      return newToasts;
    });

    return id;
  }, [generateId, position, maxToasts]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutRefs.current;
    return () => {
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, []);

  const contextValue: ToastContextType = {
    showToast,
    hideToast,
    hideAllToasts,
  };

  // Set global toast instance for use in .ts files
  useEffect(() => {
    setGlobalToastInstance({
      show: showToast,
      hide: hideToast,
      hideAll: hideAllToasts,
    });
  }, [showToast, hideToast, hideAllToasts]);

  const toastsByPosition = toasts.reduce((acc, toast) => {
    const pos = toast.position || position;
    if (!acc[pos]) {
      acc[pos] = [];
    }
    acc[pos].push(toast);
    return acc;
  }, {} as Record<ToastPosition, ToastData[]>);

  const renderToasts = () => {
    return Object.entries(toastsByPosition).map(([pos, positionToasts]) => {
      const containerClass = classNames('stepx-toast-container', `stepx-toast-${pos}`);
      
      return (
        <div key={pos} className={containerClass}>
          {(positionToasts as ToastData[]).map((toast: ToastData) => (
            <StepXToast
              key={toast.id}
              toast={toast}
              onClose={() => hideToast(toast.id)}
            />
          ))}
        </div>
      );
    });
  };

  const targetContainer = document.body; // Always render to body for consistent positioning

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(renderToasts(), targetContainer)}
    </ToastContext.Provider>
  );
};
